import { useState } from "react";
import { X, Volume2, VolumeX } from "lucide-react";
import { useGameSounds } from "@/hooks/useGameSounds";
import { useTournament } from "@/hooks/useTournament";
import { SetupScreen } from "@/components/poker/SetupScreen";
import { SummaryScreen } from "@/components/poker/SummaryScreen";
import { PokerTable } from "@/components/poker/PokerTable";
import { ActionBar } from "@/components/poker/ActionBar";
import { ActionLog } from "@/components/poker/ActionLog";
import { Hud } from "@/components/poker/Hud";

export default function GamePage() {
  const { game, summary, start, act, nextHand, quit, preAction, setPreAction } = useTournament();
  const [felt, setFelt] = useState("emerald");
  const [logOpen, setLogOpen] = useState(false);
  const sound = useGameSounds(game);

  if (!game) return <SetupScreen onStart={start} felt={felt} setFelt={setFelt} />;

  return (
    <div className="h-[100dvh] flex flex-col bg-[#090A0F] overflow-hidden">
      <Hud game={game} onQuit={quit} onOpenLog={() => setLogOpen(true)} />
      <div className="flex-1 flex min-h-0">
        <main className="flex-1 flex flex-col min-w-0 min-h-0">
          <div className="relative flex-1 min-h-0 flex items-center justify-center px-2 sm:px-6 py-2 overflow-hidden">
            <button
              data-testid="sound-toggle-button" onClick={sound.toggle} aria-label={sound.enabled ? "Mute sound" : "Enable sound"}
              className={`absolute top-3 left-3 z-30 flex items-center gap-1.5 px-3 py-2 rounded-full border text-xs font-semibold transition-colors ${sound.enabled ? "border-amber-500/50 bg-amber-500/15 text-amber-300 hover:bg-amber-500/25" : "border-white/10 bg-black/50 text-slate-400 hover:text-white"}`}
            >
              {sound.enabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
              <span data-testid="sound-toggle-state">{sound.enabled ? "Sound on" : "Sound off"}</span>
            </button>
            <PokerTable game={game} felt={felt} />
          </div>
          <div className="px-3 sm:px-6 pb-3 sm:pb-5 z-30">
            {game.phase === "handover" && (
              <div className="flex justify-center mb-2">
                <button data-testid="next-hand-button" onClick={nextHand} className="px-4 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs font-semibold text-slate-200 transition-colors">
                  {game.gameOver ? "See results" : "Deal next hand"}
                </button>
              </div>
            )}
            <ActionBar game={game} onAct={act} preAction={preAction} setPreAction={setPreAction} />
          </div>
        </main>
        <aside className="hidden lg:flex w-80 border-l border-white/5 bg-[#0d0f15]">
          <ActionLog log={game.log} />
        </aside>
      </div>
      {summary && <SummaryScreen s={summary} onAgain={() => start(game.cfg)} onClose={quit} />}
      {logOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex justify-end" onClick={() => setLogOpen(false)}>
          <div className="w-[85%] max-w-sm h-full bg-[#0d0f15] relative" onClick={(e) => e.stopPropagation()}>
            <button data-testid="close-log-button" onClick={() => setLogOpen(false)} className="absolute top-2 right-2 p-1 text-slate-400"><X size={18} /></button>
            <ActionLog log={game.log} />
          </div>
        </div>
      )}
    </div>
  );
}
